/**
 * Proposer les livres bibliques pendant qu'on tape (#254).
 *
 * Le champ « Livre » du lecteur est un texte libre : il faut connaître
 * l'orthographe exacte, ou son abréviation, pour ouvrir un passage. « Qo » et
 * « Ecclésiaste » désignent le même livre, « Jn » et « Jean » aussi, et rien à
 * l'écran ne le dit.
 *
 * Les données existent déjà : `display_names.json` donne le nom complet de
 * chaque livre, `book_map.json` ses 262 alias — abréviations, graphies sans
 * accent, formes anciennes. Il ne manquait que de s'en servir en frappe.
 *
 * Fonction pure, sans React : c'est ce qui la rend vérifiable.
 */
import { normalizeBookNameForComparison } from './bible-standardizer';

import deDisplay from '../assets/bible/de/display_names.json';
import elDisplay from '../assets/bible/el/display_names.json';
import enDisplay from '../assets/bible/en/display_names.json';
import esDisplay from '../assets/bible/es/display_names.json';
import frDisplay from '../assets/bible/fr/display_names.json';
import itDisplay from '../assets/bible/it/display_names.json';
import ruDisplay from '../assets/bible/ru/display_names.json';

import deMap from '../assets/bible/de/book_map.json';
import elMap from '../assets/bible/el/book_map.json';
import enMap from '../assets/bible/en/book_map.json';
import esMap from '../assets/bible/es/book_map.json';
import frMap from '../assets/bible/fr/book_map.json';
import itMap from '../assets/bible/it/book_map.json';
import ruMap from '../assets/bible/ru/book_map.json';

type Dictionary = { [key: string]: string };

const DISPLAY_NAMES: { [lang: string]: Dictionary } = {
  de: deDisplay, el: elDisplay, en: enDisplay, es: esDisplay,
  fr: frDisplay, it: itDisplay, ru: ruDisplay,
};

const BOOK_MAPS: { [lang: string]: Dictionary } = {
  de: deMap, el: elMap, en: enMap, es: esMap,
  fr: frMap, it: itMap, ru: ruMap,
};

export interface BookSuggestion {
  /** L'acronyme canonique, celui qui part dans l'URL. */
  acronym: string;
  /** Le nom complet, celui qu'on montre. */
  name: string;
}

/** Nombre de propositions affichées. Au-delà, la liste cesse d'aider. */
export const MAX_SUGGESTIONS = 8;

/**
 * Les livres qui correspondent à ce qui est tapé, du plus probable au moins.
 *
 * Quatre rangs, dans cet ordre : un alias exact (« qo » → Ecclésiaste), un nom
 * qui commence par la saisie, un alias qui commence par la saisie, puis un nom
 * qui la contient. Le dernier rang permet de trouver « Cantique des cantiques »
 * en tapant « cantiques », ce qu'aucun préfixe ne donnerait.
 *
 * L'ordre canonique départage les égalités : à pertinence égale, la Genèse
 * précède l'Apocalypse.
 */
export function suggestBooks(
  input: string,
  lang: string,
  limit: number = MAX_SUGGESTIONS,
): BookSuggestion[] {
  const names = DISPLAY_NAMES[lang] ?? DISPLAY_NAMES.fr;
  const aliases = BOOK_MAPS[lang] ?? BOOK_MAPS.fr;

  const needle = normalizeBookNameForComparison(input);
  if (needle.length === 0) return [];

  // Acronyme -> meilleur rang obtenu. Un livre peut être atteint par son nom
  // et par plusieurs alias ; seul le meilleur compte.
  const ranks = new Map<string, number>();
  const keep = (acronym: string, rank: number) => {
    if (!names[acronym]) return; // un alias peut pointer hors du canon affiché
    const previous = ranks.get(acronym);
    if (previous === undefined || rank < previous) ranks.set(acronym, rank);
  };

  const exact = aliases[needle];
  if (exact) keep(exact, 0);

  for (const [acronym, name] of Object.entries(names)) {
    const normalized = normalizeBookNameForComparison(name);
    if (normalized.startsWith(needle)) keep(acronym, 1);
    else if (normalized.includes(needle)) keep(acronym, 3);
    // L'acronyme lui-même compte comme un nom : on tape souvent « Ps ».
    if (normalizeBookNameForComparison(acronym).startsWith(needle)) {
      keep(acronym, 1);
    }
  }

  for (const [alias, acronym] of Object.entries(aliases)) {
    if (alias.startsWith(needle)) keep(acronym, 2);
  }

  // L'ordre des clés de `display_names` est l'ordre canonique : s'y appuyer
  // evite de trier des noms par alphabet, ce qui melangerait les testaments.
  const canonical = Object.keys(names);
  const ordered = canonical
    .filter((acronym) => ranks.has(acronym))
    .sort((a, b) => {
      const byRank = ranks.get(a)! - ranks.get(b)!;
      return byRank !== 0 ? byRank : canonical.indexOf(a) - canonical.indexOf(b);
    });

  // Un même livre porte parfois deux acronymes — l'Ecclésiaste est `Qo` et
  // `Ec` dans cinq langues sur sept. Deux lignes identiques dans la liste
  // donneraient l'impression d'un choix à faire là où il n'y en a pas.
  const seen = new Set<string>();
  const unique: BookSuggestion[] = [];
  for (const acronym of ordered) {
    const name = names[acronym];
    if (seen.has(name)) continue;
    seen.add(name);
    unique.push({ acronym, name });
    if (unique.length === limit) break;
  }
  return unique;
}
